import {
  DEFAULT_PALETTE_ID,
  PALETTE_COOKIE,
  fixedTone,
  resolvePaletteId,
} from "@/lib/palettes";

export type ThemeMode = "dark" | "light";

export type ThemePreference = ThemeMode | "system";

const THEME_COOKIE = "gg-theme";

export function resolveTheme(
  pref: ThemePreference | null | undefined,
): ThemeMode {
  if (pref === "dark" || pref === "light") return pref;
  if (
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
  ) {
    return "dark";
  }
  return "light";
}

export function getClientThemeFallback(): ThemeMode {
  if (typeof window === "undefined") return "light";
  try {
    const stored = localStorage.getItem("theme");
    if (stored === "light" || stored === "dark") return stored;
  } catch {
    /* ignore */
  }
  if (window.matchMedia?.("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "light";
}

export function applyTheme(theme: ThemeMode): void {
  if (typeof document === "undefined") return;
  // A fixed palette owns the page tone; the Light / Dark choice is still saved for when Clear returns.
  if (document.documentElement.getAttribute("data-tone-locked") !== "true") {
    document.documentElement.setAttribute("data-theme", theme);
  }
  try {
    localStorage.setItem("theme", theme);
  } catch {
    /* ignore */
  }
  document.cookie = `${THEME_COOKIE}=${theme}; path=/; max-age=31536000; samesite=lax`;
}

/** The palette this browser last used (cookie first, then localStorage), for logged-out pages and first paint. */
export function getClientPaletteFallback(): string {
  if (typeof document === "undefined") return DEFAULT_PALETTE_ID;
  const fromCookie = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${PALETTE_COOKIE}=`))
    ?.slice(PALETTE_COOKIE.length + 1);
  if (fromCookie) return resolvePaletteId(decodeURIComponent(fromCookie));
  try {
    return resolvePaletteId(localStorage.getItem("palette"));
  } catch {
    return DEFAULT_PALETTE_ID;
  }
}

/**
 * Show a palette. A fixed palette (Omarchy) brings its own light or dark tone and leaves the saved
 * Light / Dark choice alone, so switching back to Clear restores it. The adaptive Clear palette uses `mode`.
 */
export function applyAppearance(paletteId: unknown, mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  const id = resolvePaletteId(paletteId);
  const tone = fixedTone(id);
  const root = document.documentElement;
  root.setAttribute("data-palette", id);
  if (tone) {
    root.setAttribute("data-theme", tone);
    root.setAttribute("data-tone-locked", "true");
  } else {
    root.removeAttribute("data-tone-locked");
    applyTheme(mode);
  }
  try {
    localStorage.setItem("palette", id);
  } catch {
    /* ignore */
  }
  document.cookie = `${PALETTE_COOKIE}=${id}; path=/; max-age=31536000; samesite=lax`;
}

export function toggleTheme(current?: ThemeMode | null): ThemeMode {
  const next: ThemeMode =
    (current === "light" || current === "dark"
      ? current
      : getClientThemeFallback()) === "dark"
      ? "light"
      : "dark";
  applyTheme(next);
  return next;
}
